import React, { useMemo } from 'react';

import { useAtom } from 'jotai';
import { Segment, Grid } from 'semantic-ui-react';

import ChannelOptimizationChart from './chart';
import DailyChart, { SimplePlot } from './DailyChart';
import { useComputedHistograms, useKPIAndTargetSelection } from './hooks';
import ChannelOptimizationLoader from './loaders';
import { ROIHatChart } from './ROIChart';
import SpendStats from './SpendStats';
import { useChannelTactics } from './useChannelTactics';
import { kpiAtom } from '../../../atoms';
import { getRevenueFromResponseCurve } from '../forecast/forecastData';

const ChannelOptimization = ({ rawData, trainingInfo, responseCurves, predRevenueByDate }) => {
  const [kpi] = useAtom(kpiAtom);
  const isConversionKPI = kpi === 'conversions';
  const { chartType } = useKPIAndTargetSelection(isConversionKPI);

  const { trainingData } = trainingInfo;
  const { spend_effect: spendEffect, roi_hat: featureROI } = rawData;

  const { allHistograms, channels, features } = useComputedHistograms(rawData, trainingData);

  const {
    selectedChannel,
    selectedTactic,
    selectedFeature,
    ChannelSelector,
    TacticSelector,
  } = useChannelTactics(channels, features);

  const histogram = useMemo(() => {
    if (allHistograms && selectedTactic && selectedFeature) {
      return allHistograms[selectedFeature.value];
    }
    return [];
  }, [allHistograms, selectedChannel, selectedTactic]);

  const responseCurve = useMemo(() => {
    if (!responseCurves || !selectedFeature) return;
    return responseCurves[selectedFeature.value];
  }, [responseCurves, selectedFeature]);

  const currentSpendEffectData = useMemo(() => (
    (!rawData || !selectedFeature) ? [] : spendEffect.filter(x => x.feature_name === selectedFeature.value)
  ), [rawData, selectedFeature]);

  const currentFeatureSpendTimeseries = useMemo(() => {
    /* eslint-disable no-param-reassign */
    if (!rawData || !trainingData || !responseCurve) return [];

    const _byDay = trainingData.map(x => {
      const totalSpend = Object.entries(x).reduce((acc, [key, value]) => (key === 'date' || key.slice(-2) !== '_S' ? acc : acc + value), 0);
      const revenue = getRevenueFromResponseCurve(responseCurve, x[selectedFeature.value]);
      const totalRevenue = predRevenueByDate[x.date];

      return {
        day: x.date,
        spend: x[selectedFeature.value],
        totalSpend,
        revenue,
        totalRevenue,
      };
    });

    const byDay = _byDay.reduce((byDate, row, position) => {
      const { revenue } = row;

      currentSpendEffectData.forEach(spendEffectDay => {
        const { day_in_window, spend_effect } = spendEffectDay;
        const targetRow = _byDay[position + day_in_window];
        if (!targetRow) return;

        const { day } = targetRow;
        const newRow = byDate[day] || { ...targetRow, normalizedRevenue: 0 };
        const contribution = revenue * (spend_effect || 0);
        newRow.normalizedRevenue += contribution;
        byDate[day] = newRow;
      });
      return byDate;
    }, {});

    const finalData = (currentSpendEffectData.length > 0) ? byDay : _byDay;

    const withCalcs = Object.values(finalData).map(row => {
      row.revenue = row.normalizedRevenue || row.revenue;
      row.percentOfTotalSpend = row.spend / row.totalSpend;
      row.percentOfTotalRevenue = row.revenue / row.totalRevenue;
      row.roas = row.revenue / row.spend;
      row.cpa = row.spend / row.revenue;
      return row;
    });
    /* eslint-enable no-param-reassign */
    return withCalcs;
  }, [rawData, selectedFeature]);

  const data = currentFeatureSpendTimeseries;
  const averageSpend = data.reduce((acc, x) => acc + x.spend, 0) / data.length;
  const currentSpend = averageSpend;

  // calculate expected revenue for current spend
  const currentRevenue = useMemo(() => {
    if (!currentSpend) return;
    return getRevenueFromResponseCurve(responseCurve, currentSpend);
  }, [responseCurve, currentSpend]);

  const spendEffectData = useMemo(
    () => currentSpendEffectData
      .map(x => {
        // handle extremely small values of x.spend_effect
        const calculatedSpendEffect = parseInt(x.spend_effect * 1000000000000000) / 1000000000000000;
        return Object.assign(x, { spendEffect: calculatedSpendEffect });
      })
      .sort((a, b) => a.day_in_window - b.day_in_window)
      .map((x, i) => {
        const previous = i > 0 ? currentSpendEffectData[i - 1] : { cumulativeEffect: 0 };
        const cumulativeEffect = previous.cumulativeEffect + x.spend_effect;
        const cumulativeEffectRounded = Math.round(cumulativeEffect * 1000000000000000, 2) / 1000000000000000;
        return Object.assign(x, { cumulativeEffect, cumulativeEffectRounded });
      })
      .filter((x, i) => {
        const previous = i > 0 ? currentSpendEffectData[i - 1] : { cumulativeEffect: 0 };
        return previous.cumulativeEffect === 0 || x.spendEffect > 0;
      }),
    [currentSpendEffectData],
  );

  const currentKPI = isConversionKPI ? currentRevenue : currentRevenue;

  if (!channels?.length || !responseCurve?.length) return <ChannelOptimizationLoader />;

  const currentROI = featureROI.find(x => x.feature === selectedFeature.value) || {};
  const hasCurrentROI = currentROI.roi !== undefined;

  return (
    <>
      <ChannelSelector />
      <Segment attached="bottom" style={{ border: '1px solid #e7eafc' }}>
        <TacticSelector />
        <br />
        <DailyChart
          name={selectedFeature.value}
          data={data}
          isConversionKPI={isConversionKPI}
        />

        <Grid style={{ padding: 20, paddingTop: 0 }}>
          <Grid.Row>
            <h3>Spend Distribution</h3>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <div style={{ position: 'relative' }}>
                <ChannelOptimizationChart {...{
                  isConversionKPI,
                  chartType,
                  responseCurve,
                  currentSpend,
                  currentKPI,
                  // targetSpend,
                  // predictedKPI,
                  histogram,
                  histogramOnly: true,
                  height: 250,
                }}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={8}>
              { data && <SpendStats data={data} /> }
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Grid style={{ padding: 20, paddingTop: 0 }}>
          <Grid.Row>
            <h3>Predicted Performance</h3>
          </Grid.Row>
          { hasCurrentROI && (
          <Grid.Row>
            <h5 width="100%">{`${isConversionKPI ? 'CPA' : 'ROI'} confidence at average spend`}</h5>
            <ROIHatChart ROIHatData={[currentROI]} {...{ isConversionKPI }} />
          </Grid.Row>
          )}
          <Grid.Row>
            <h5>{`Diminishing returns of ${isConversionKPI ? 'CPA' : 'ROI'} based on spend saturation`}</h5>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <div style={{ position: 'relative' }}>
                <ChannelOptimizationChart {...{
                  isConversionKPI,
                  chartType,
                  responseCurve,
                  currentSpend,
                  currentKPI,
                  // targetSpend,
                  // predictedKPI,
                  // histogram
                  height: 250,
                }}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={8}>
              <div style={{ position: 'relative' }}>
                <ChannelOptimizationChart {...{
                  isConversionKPI,
                  chartType: 'metric',
                  responseCurve,
                  currentSpend,
                  currentKPI,
                  // targetSpend,
                  // predictedKPI,
                  // histogram
                  height: 250,
                }}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          {currentSpendEffectData.length > 0 && (
            <>
              <Grid.Row>
                <h5>Peak effectiveness / delay</h5>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  <div style={{ position: 'relative' }}>
                    <SimplePlot
                      margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
                      type="area"
                      dataKey="spendEffect"
                      timeKey="day_in_window"
                      data={spendEffectData}
                      showAxis
                    />
                  </div>
                </Grid.Column>
                <Grid.Column width={8}>
                  <div style={{ position: 'relative' }}>
                    <SimplePlot
                      margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
                      type="area"
                      dataKey="cumulativeEffectRounded"
                      timeKey="day_in_window"
                      data={spendEffectData}
                      showAxis
                      yTickCount={3}
                      yTicks={[0, 0.5, 1]}
                      domain={[0, 1]}
                      tooltipFormatter={v => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
            </>
          )}
        </Grid>
      </Segment>
    </>
  );
};

export default ChannelOptimization;
